import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import React, { useCallback, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import { OptimizedBackgroundImage } from "../common/OptimizedImage";
import ViewMoreCard from "../common/ViewMoreCard";
import { useInputMode } from "../../utils/hooks/useInputMode";
import LiveStartCountdown from "../common/LiveStartCountdown";
import {
  getDisplayYear,
  getFutureLiveStartTimestamp,
  isLiveContent,
} from "../../utils/liveStartCountdown";
import {
  focusHomeMenu,
  focusHomeTopSlider,
  resetRowTransform,
  resetRowTransformAfterBlur,
  resetRowTransformIfUnfocused,
  setRtlScrollStart,
  syncFocusedRowTransform,
} from "./focusHelpers";
import NextPlayGuideHelper from "../../utils/nextPlayGuideHelper";

const MovieItem = ({ item = {}, onFocus, onBlur, isFirst, focusKey, nextPlayGuide }) => {
  const { contentPhotos, slug, title, year, kind, contentType } = item?.content || {};
  const navigate = useNavigate();
  const content = item?.content || {};
  const liveStartTimestamp = isLiveContent(content) ? getFutureLiveStartTimestamp(content) : null;
  const displayYear = getDisplayYear(year);
  const handleNavigation = () => {
    const contentKind = contentType?.kind || kind;
    const path = `/${contentKind}/${slug}`;
    if (contentKind === "music" && nextPlayGuide) {
      navigate(path, {
        state: NextPlayGuideHelper.withGuideNavigationState(null, nextPlayGuide),
      });
    } else {
      navigate(path);
    }
  };
  const { focused, ref, focusSelf } = useFocusable({
    focusKey,
    onFocus,
    onBlur,
    onEnterPress: handleNavigation,
    onArrowPress: (direction) => {
      if (direction === "up") {
        return focusHomeTopSlider();
      }

      if (direction === "right" && isFirst) {
        return focusHomeMenu();
      }
      return true;
    },
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized image with LQIP and WebP support
  const imageData = getOptimizedImageForType("infinitySliderCover", contentPhotos, "TV", {
    quality: 90,
    width: 300,
    format: "jpeg",
  });

  return (
    <div
      ref={ref}
      className={"legacy-turkylar-card" + (focused ? " is-focused" : "")}
      onClick={handleNavigation}
      onMouseEnter={handleMouseEnter}
    >
      <OptimizedBackgroundImage
        src={imageData.src}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "10px",
        }}
      />
      <div className="legacy-tv-card-focus-border" />
      <div className="legacy-tv-card-overlay">
        <span className="legacy-turkylar-title-text">{title}</span>
        {displayYear && <span className="legacy-turkylar-year-text">{displayYear}</span>}
      </div>
      {liveStartTimestamp && (
        <LiveStartCountdown
          startTimestamp={liveStartTimestamp}
          style={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 15,
            justifyContent: "center",
          }}
        />
      )}
    </div>
  );
};

export const Turkilar = ({ movies, onFocus, rowTitle, id, index, actionLink = null, onMoreTap = null, nextPlayGuide = null }) => {
  const itemFocusKeyPrefix = "turkilar_item_" + id + "_";
  const { ref, focusKey } = useFocusable({
    focusKey: "turkilar_" + id,
    focusable: false,
    trackChildren: true,
    autoRestoreFocus: true,
    preferredChildFocusKey: itemFocusKeyPrefix + "0",
  });

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);
  useLayoutEffect(() => {
    var delays = [0, 80, 220, 600, 1400, 2600];
    resetRowTransform(innerRef);
    if (scrollingRef.current) {
      setRtlScrollStart(scrollingRef.current);
    }
    var timers = delays.map(function (delay) {
      return setTimeout(function () {
        resetRowTransformIfUnfocused(innerRef);
        if (scrollingRef.current) {
          setRtlScrollStart(scrollingRef.current);
        }
      }, delay);
    });

    return function () {
      timers.forEach(function (timer) {
        clearTimeout(timer);
      });
    };
  }, [movies.length]);
  const { isMouseFocus } = useInputMode();
  const onAssetFocus = useCallback(
    (props) => {
      if (typeof onFocus === "function") onFocus(props);
      if (isMouseFocus) return; // Skip horizontal scroll on mouse hover
      syncFocusedRowTransform({
        innerRef,
        scrollerRef: scrollingRef,
        leftPadding: 300,
      });
    },
    [scrollingRef, isMouseFocus, onFocus]
  );
  const onAssetBlur = useCallback(
    () => {
      resetRowTransformAfterBlur(innerRef);
    },
    []
  );

  if (movies.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-turkylar-row" ref={ref} tabIndex={index}>
        <h2 className="legacy-turkylar-title">{rowTitle}</h2>
        <div
          className="legacy-turkylar-scroller"
          ref={scrollingRef}
        >
          <div
            className="legacy-turkylar-content"
            ref={innerRef}
            style={{ transform: "translateX(0px)" }}
          >
            {movies?.map((item, idx) => {
              return (
                <MovieItem
                  item={item}
                  key={"tlr_" + item.content?.slug}
                  onFocus={(e) => {
                    onAssetFocus(e);
                  }}
                  onBlur={onAssetBlur}
                  isFirst={idx === 0}
                  focusKey={itemFocusKeyPrefix + idx}
                  nextPlayGuide={nextPlayGuide}
                />
              );
            })}
            <div style={{ paddingLeft: "2rem" }}>
              <ViewMoreCard
                width={450}
                height={250}
                actionLink={actionLink}
                onMoreTap={onMoreTap}
                focusKey={`view_more_tlr`}
                onArrowPress={(direction) => {
                  if (direction === "up") {
                    return focusHomeTopSlider();
                  }
                  return true;
                }}
                onFocus={(e) => {
                  onAssetFocus(e);
                }}
                onBlur={onAssetBlur}
              />
            </div>
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};
